import { CHUNK_SIZE, type ChunkCoord, chunkOfTile, type TilePos } from "./types";

/*
 * Which chunks the client asks for meshes of, and in what order.
 *
 * This is a *renderer* decision and it is separated out because it is the only
 * interesting arithmetic in the streaming path. The simulation streams in two
 * dimensions — `Command::Interest` generates whole chunk columns, and a column
 * spans the world's entire 88-level z band (`sim_core`'s worldgen) — so the
 * first interest a client sends materialises thousands of chunks, most of them
 * buried and invisible. The wasm layer deliberately refuses to narrow that for
 * us: "a host that only draws a few z-levels should not pay for the ones it
 * hides" (`TickSummary::dirty_chunks`). So the narrowing happens here.
 *
 * The window is a square of columns around the player, and within each column a
 * *band* of z-levels. The band is the part worth explaining, because the obvious
 * version — a flat window of `playerZ ± n` — is both bigger and worse:
 *
 * - Terrain is not flat. Measured on seed 0x5eed, ground level varies by 21
 *   tiles across a 9×9-chunk square and by 61 tiles over an 800-tile walk, so a
 *   band centred on the player either clips a nearby valley's cliff face (a hole
 *   you can see through) or has to be wide enough for the worst case everywhere.
 * - A band that reaches from the player's own level to the column's surface
 *   never has a hole in it, because everything between those two levels is
 *   either the ground the player stands on, the wall of the valley they are
 *   looking into, or air — and air meshes to nothing.
 *
 * Measured, seed 0x5eed, radius 4, standing on the surface: a flat `±12` window
 * meshes 2,025 slabs of which 867 have geometry; the band below meshes 1,295 of
 * which 739 do. A third less work for strictly better coverage.
 *
 * # The top of the band is the world's number, not ours
 *
 * The band's *bottom* is a guess with a margin on it and always was. Its **top**
 * was a guess too until wave 1.6, and that one was wrong: it read the column's
 * highest ground and added four, on the reasoning that "above the surface there
 * is nothing but air". True while a town wall was three tiles and a roof was a
 * plate; false the day a house got two storeys and a stepped gable, and the
 * owner walked a street where the roofs were missing from below and there from
 * the rooftop (`docs/plans/phase-7-wave-1.6-notes.md`, item 10). The
 * chunk-aligned cut lines and the floating eaves were the same bug seen from
 * inside: the band lifts for a whole 16×16 column, so one tall corner of a
 * column dragged the rest of it into view and nothing else.
 *
 * The world already computes the number this needed — the level generation's own
 * sky fast path stops at, which counts a ramp's top, a trunk, a crown, a roof
 * course and an eave hanging one tile past a wall — so the client asks for it
 * instead of guessing ({@link ColumnSpan.reach},
 * `sim_core::Snapshot::column_reach`). A guess in the renderer about what the
 * generator builds is a guess that goes stale every time the generator gets
 * better at building, which is the only lesson here worth writing down.
 */

/**
 * How far interest reaches, in chunks — the square the simulation is asked to
 * generate around the player.
 *
 * Four is 9×9 columns, which is 7,128 chunks and about 54 ms of generation in
 * one tick the first time (measured in Node; the browser is the same code).
 * That cost lands once, behind the click-to-play overlay, and afterwards
 * crossing a chunk boundary generates one strip of nine columns. Five would look
 * further and cost half again as much; three starts to show the fog eating the
 * horizon.
 */
export const INTEREST_RADIUS_CHUNKS = 4;

/**
 * How far below the lower of (the column's ground, the player) the band reaches.
 *
 * Six tiles: enough that standing at the lip of a hole shows its floor and its
 * walls, and that the first few levels of a dug shaft are drawn before the band
 * follows the player down into it.
 */
export const MESH_Z_BELOW = 6;

/**
 * How far above the higher of (the column's reach, the player) the band reaches.
 *
 * **Three tiles, and it is slack rather than coverage.** Coverage is
 * {@link ColumnSpan.reach}'s job now: the world states the level above which a
 * chunk column is sky for good, so a band that stops there stops exactly where
 * geometry does and the margin buys nothing above a column standing on its own.
 * What it still has to buy is the *player* term — the band's top is also
 * `playerZ + this`, and the player's z may be {@link Z_HYSTERESIS} tiles stale
 * before the window is recomputed. So: two for the hysteresis, one so the slab
 * over a player's head is drawn when they are standing on the highest tile a
 * column has.
 *
 * It was four while it was the whole coverage rule, and dropping it to three is
 * how the change stays honest about cost: a bare hillside column's reach is its
 * ground plus one (a ramp's top), so `reach + 3` is the `ground + 4` it always
 * was and a treeless column meshes exactly the band it always did. Every extra
 * slab lands on a column that holds something tall.
 *
 * **And the one slab it can take away is provably empty.** The new top drops
 * below the old one only where the player's own level decides both — and a
 * player above a column's reach is a player standing over ground the world has
 * finished with, so the level given up is four or more above the last thing that
 * column will ever hold.
 *
 * Measured, seed 0x5, 170 years, radius 4, standing in settlement 9's street:
 * **1,517 slabs before, 1,825 after**, and of the 333 the top gained, **133 hold
 * geometry the old band never meshed** — the roofs. In open country 300 tiles
 * out: 1,474 → 1,615, of which 36 hold geometry, because a tall tree is trunk
 * plus crown and reaches six or seven levels over its own ground. The old band
 * had been shaving the tops off woods for as long as it had been losing roofs;
 * nobody had reported that one.
 */
export const MESH_Z_ABOVE = 3;

/**
 * How far the player may move vertically before the window is recomputed.
 *
 * Recomputing means walking the whole window — about 1,300 chunk coordinates —
 * so doing it on every tile of a hillside descent is wasted work. Two tiles of
 * slack, which is why {@link MESH_Z_BELOW} and {@link MESH_Z_ABOVE} are two
 * larger than the margins the window is meant to guarantee: four of hole below
 * and one of headroom above.
 */
const Z_HYSTERESIS = 2;

/** How far down and how far up a chunk column reaches, in tiles. */
export interface ColumnSpan {
  /** The lowest ground level in the column's 16×16 footprint. */
  readonly lowest: number;

  /**
   * The highest level anything in the footprint will **ever** stand at: above
   * it the column is sky, for good.
   *
   * `sim_core::Snapshot::column_reach`, and the two words in that sentence are
   * both load-bearing. *Anything*: a ramp's top, a trunk, a crown reaching in
   * from the column next door, a roof course, an eave hanging past a wall.
   * *Ever*: the town is read at the last stage its plan has, so growth cannot
   * raise this and the value below is safe to memoise for the life of a
   * session — which is what {@link MeshWindow} does with it.
   */
  readonly reach: number;
}

/**
 * Where the window gets its columns' spans, asked in a batch. One answer per
 * column, in order. Pure: the same column always answers the same.
 *
 * **A batch and not a column, and that is a boundary decision showing through.**
 * Deriving a town's plan is the expensive half of answering
 * {@link ColumnSpan.reach} and reading a column of it is the cheap one, so the
 * simulation answers about a *set* of columns and lays the town out once
 * (`sim_core::Snapshot::column_reach`, `Snapshot::open_ground_near` before it).
 * Measured on seed 0x5 at the moment a session first asks — before a single
 * chunk has been generated, so nothing is cached anywhere — a window's
 * eighty-one columns cost **2,413 ms asked one at a time and 45 ms asked
 * together**. Once the first interest has folded the town plans it is 9 ms, and
 * a chunk crossing's fresh strip of nine is 1.2 ms.
 */
export type ColumnSpanSource = (
  columns: readonly (readonly [number, number])[],
) => readonly ColumnSpan[];

/** The z-levels the world generates at all: nothing outside this is worth asking for. */
export interface ZBand {
  readonly min: number;
  readonly max: number;
}

/**
 * The set of chunks the client wants meshes for, and the order it wants them in.
 *
 * Holds no meshes and talks to nothing: it answers "is this chunk wanted",
 * "which chunks are wanted" and "how urgent is this one", and `world/wasm.ts`
 * does the rest. That split is what lets `dev/selfcheck.ts` check the arithmetic
 * without a simulation.
 */
export class MeshWindow {
  /**
   * The half-width of the square of columns, in chunks.
   *
   * Not a constant of this class: it is whatever radius the client last asked
   * the *world* for ({@link MeshWindow.moveTo}), because meshing a chunk the
   * simulation was not asked to generate would draw an empty slab and meshing
   * fewer than were generated would waste the generation.
   */
  private radiusChunks: number;

  private readonly band: ZBand;
  private readonly spanOf: ColumnSpanSource;

  /**
   * Column spans by column key, memoised forever.
   *
   * Both halves are pure functions of `(seed, settlement records, column)` — the
   * simulation re-derives them from the elevation field and the town plan rather
   * than reading the map, which is why they answer for columns that do not exist
   * yet (`WasmSim::ground_level`, `WasmSim::column_reach`). A value that cannot
   * change is a value worth caching, and this one is asked for thousands of
   * times per window rebuild.
   *
   * **Nothing invalidates it, and that is a property of the answers rather than
   * an optimism about them.** A ground level is fixed at worldgen. A reach is
   * read at the town plan's *last* growth stage, so the day a lot arrives and a
   * shell goes up the number was already counted; a felled tree lowers the world
   * and a dug shaft only ever opens it. The one thing the cache cannot see is a
   * settlement **founded** after a column was cached — which cannot bite in
   * practice, because a column is asked about on the tick the client first
   * expresses interest in it, which is the tick its ground is made on.
   *
   * Never evicted: it holds one small entry per chunk column the player has been
   * near, so an hour of walking is a few thousand of them. Evicting would mean
   * re-deriving a town plan for a column the player turned around and walked
   * back into, which is the common case.
   */
  private readonly spanCache = new Map<string, ColumnSpan>();

  /** The player's tile as of the last {@link MeshWindow.moveTo} that moved the window. */
  private center: ChunkCoord = { cx: 0, cy: 0, z: 0 };
  private centerTile: TilePos = { x: 0, y: 0, z: 0 };
  private placed = false;

  constructor(radiusChunks: number, band: ZBand, spanOf: ColumnSpanSource) {
    this.radiusChunks = radiusChunks;
    this.band = band;
    this.spanOf = spanOf;
  }

  /** The chunk the window is centred on. */
  get centerChunk(): ChunkCoord {
    return this.center;
  }

  /**
   * Re-centres the window on a tile, and returns `true` if that changed which
   * chunks are wanted.
   *
   * Called every tick with the player's tile. Most calls answer `false`: the
   * window only moves when the player crosses a chunk boundary, changes the
   * radius, or climbs past {@link Z_HYSTERESIS} tiles.
   */
  moveTo(tile: TilePos, radiusChunks: number): boolean {
    const chunk = chunkOfTile(tile.x, tile.y, tile.z);
    const moved =
      !this.placed ||
      radiusChunks !== this.radiusChunks ||
      chunk.cx !== this.center.cx ||
      chunk.cy !== this.center.cy ||
      Math.abs(tile.z - this.centerTile.z) > Z_HYSTERESIS;

    if (!moved) {
      return false;
    }

    this.placed = true;
    this.radiusChunks = radiusChunks;
    this.center = chunk;
    this.centerTile = tile;

    return true;
  }

  /** Returns `true` when the chunk is inside the window. */
  contains(coord: ChunkCoord): boolean {
    if (
      Math.abs(coord.cx - this.center.cx) > this.radiusChunks ||
      Math.abs(coord.cy - this.center.cy) > this.radiusChunks
    ) {
      return false;
    }

    const band = this.bandOf(coord.cx, coord.cy);

    return coord.z >= band.lowest && coord.z <= band.highest;
  }

  /** Calls `visit` with every chunk in the window, once each, in scan order. */
  forEach(visit: (coord: ChunkCoord) => void): void {
    const radius = this.radiusChunks;

    // The one place that knows the whole square before it needs any of it, and
    // therefore the one place the batch can be a batch ({@link ColumnSpanSource}).
    // Everything else asks about a column it is already holding.
    this.fillSpans();

    for (let cx = this.center.cx - radius; cx <= this.center.cx + radius; cx += 1) {
      for (let cy = this.center.cy - radius; cy <= this.center.cy + radius; cy += 1) {
        const band = this.bandOf(cx, cy);

        for (let z = band.lowest; z <= band.highest; z += 1) {
          visit({ cx, cy, z });
        }
      }
    }
  }

  /**
   * Returns how far a chunk is from the player, squared, in tiles.
   *
   * The sort key for the remesh queue: the ground under the player has to exist
   * before the horizon does, and a squared distance in *tile* units is the one
   * metric that compares a column's distance with a z-level's on equal terms —
   * sixteen levels down is as far away as one chunk across.
   */
  priority(coord: ChunkCoord): number {
    const dx = coord.cx * CHUNK_SIZE + CHUNK_SIZE / 2 - this.centerTile.x;
    const dy = coord.cy * CHUNK_SIZE + CHUNK_SIZE / 2 - this.centerTile.y;
    const dz = coord.z - this.centerTile.z;

    return dx * dx + dy * dy + dz * dz;
  }

  /**
   * The z-levels wanted in one column, clamped to the world's band.
   *
   * Asymmetric on purpose, and the two halves have different standing: the
   * bottom is *the lower of the ground and the player*, minus a margin, because
   * what is under a surface is a guess about how deep a hole the player is
   * looking into. The top is *the higher of the column's reach and the player*,
   * and the reach is not a guess at all — it is the level the world itself
   * stops building at (this module's docs).
   */
  private bandOf(cx: number, cy: number): { lowest: number; highest: number } {
    const span = this.spanAt(cx, cy);
    const playerZ = this.centerTile.z;

    return {
      lowest: Math.max(this.band.min, Math.min(span.lowest, playerZ) - MESH_Z_BELOW),
      highest: Math.min(this.band.max, Math.max(span.reach, playerZ) + MESH_Z_ABOVE),
    };
  }

  private spanAt(cx: number, cy: number): ColumnSpan {
    const cached = this.spanCache.get(`${cx},${cy}`);

    if (cached !== undefined) {
      return cached;
    }

    // A batch of one. Only reached for a column the square does not cover — a
    // chunk the tick reported dirty at the frontier — because {@link forEach}
    // fills the square before anything reads it.
    //
    // The fallback is the player's own level, which is the flat window this
    // module's opening paragraph calls bigger and worse: a source that answered
    // short would be a bug across the wasm boundary, and the degenerate band is
    // wasteful rather than holed.
    const answered = this.remember([[cx, cy]])[0];

    return answered ?? { lowest: this.centerTile.z, reach: this.centerTile.z };
  }

  /** Asks for every column of the square that is not cached yet, in one call. */
  private fillSpans(): void {
    const radius = this.radiusChunks;
    const wanted: [number, number][] = [];

    for (let cx = this.center.cx - radius; cx <= this.center.cx + radius; cx += 1) {
      for (let cy = this.center.cy - radius; cy <= this.center.cy + radius; cy += 1) {
        if (!this.spanCache.has(`${cx},${cy}`)) {
          wanted.push([cx, cy]);
        }
      }
    }

    if (wanted.length > 0) {
      this.remember(wanted);
    }
  }

  /** Asks the source about a set of columns and files every answer it gives. */
  private remember(columns: readonly (readonly [number, number])[]): readonly ColumnSpan[] {
    const spans = this.spanOf(columns);

    // Zipped by index against the columns asked for, which is the source's own
    // contract — a short answer files what arrived and leaves the rest to be
    // asked again rather than pairing a column with somebody else's span.
    for (const [slot, column] of columns.entries()) {
      const span = spans[slot];

      if (span !== undefined) {
        this.spanCache.set(`${column[0]},${column[1]}`, span);
      }
    }

    return spans;
  }
}

/**
 * Calls `visit` with every chunk whose mesh a change at one tile can alter.
 *
 * The mesher culls a slab's faces against a padded neighbourhood — the chunk
 * plus a one-tile skirt in x and y and the slabs above and below (`mesher`'s
 * `slab` module) — so changing one tile changes what up to eight chunks *draw*
 * without changing a single tile in them. The dirty list a tick reports says
 * only which chunks *changed*, and says so deliberately: "expanding the list is
 * the client's call because the neighbourhood is the mesher's radius, not the
 * simulation's" (`TickSummary::dirty_chunks`). This is the client making it.
 *
 * Keyed on the changed *tile* rather than on its chunk, which is the tight
 * version of the same rule: only chunks within one tile of the change can see
 * it, so a dig in the middle of a chunk expands to one chunk and a dig on a
 * corner seam to eight.
 */
export function forEachChunkNear(pos: TilePos, visit: (coord: ChunkCoord) => void): void {
  const seen = new Set<string>();

  for (let dx = -1; dx <= 1; dx += 1) {
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dz = -1; dz <= 1; dz += 1) {
        const coord = chunkOfTile(pos.x + dx, pos.y + dy, pos.z + dz);
        const key = `${coord.cx},${coord.cy},${coord.z}`;

        if (seen.has(key)) {
          continue;
        }

        seen.add(key);
        visit(coord);
      }
    }
  }
}
